import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useBlog } from '../context/BlogContext';
import { useAuth } from '../context/AuthContext';
import BlogCard from '../components/BlogCard';
import PageTransition from '../components/PageTransition';
import SubscribeBox from '../components/SubscribeBox';
import { motion } from 'framer-motion';
import { hasRichContent } from '../utils/richContent';

const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, delay, ease: [0.4, 0, 0.2, 1] },
});

export default function Home() {
  const { posts, loading, siteSettings, settingsLoading } = useBlog();
  const { user } = useAuth();
  const [activeTag, setActiveTag] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const POSTS_PER_PAGE = 3;

  const hasCustomHome = hasRichContent(siteSettings?.homeContent || '');

  // Extract unique tags from all posts
  const allTags = useMemo(() => {
    const tagSet = new Set();
    posts.forEach((p) => (p.tags || []).forEach((t) => tagSet.add(t)));
    return Array.from(tagSet).sort();
  }, [posts]);

  // Filter posts by active tag and search query
  const filteredPosts = useMemo(() => {
    let result = posts;
    if (activeTag) {
      result = result.filter((p) => (p.tags || []).includes(activeTag));
    }
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      result = result.filter((p) => (p.title || '').toLowerCase().includes(q));
    }
    return result;
  }, [posts, activeTag, searchQuery]);

  const totalPages = Math.ceil(filteredPosts.length / POSTS_PER_PAGE);
  const paginatedPosts = filteredPosts.slice((page - 1) * POSTS_PER_PAGE, page * POSTS_PER_PAGE);

  return (
    <PageTransition>
      <div style={{ padding: '3rem 0 4rem' }}>

        {!settingsLoading && (hasCustomHome ? (
          /* Custom homepage content from admin dashboard */
          <motion.div {...fade(0.1)} className="container rich-content" style={{ maxWidth: '900px', margin: '0 auto 4rem' }}>
            <div dangerouslySetInnerHTML={{ __html: siteSettings.homeContent }} />
          </motion.div>
        ) : (
          <>
            {/* Hero */}
            <div className="container" style={{ textAlign: 'center', maxWidth: '900px', margin: '0 auto 4rem' }}>
              <motion.div {...fade(0.1)} style={{ fontSize: '0.78rem', letterSpacing: '0.15em', textTransform: 'uppercase', color: 'var(--text-tertiary)', marginBottom: '1rem' }}>
                The Mushroom Understory
              </motion.div>
              <motion.h1 {...fade(0.2)} style={{
                fontFamily: 'var(--font-serif)', fontSize: 'clamp(2.2rem, 5vw, 3rem)',
                lineHeight: 1.15, fontWeight: 400, marginBottom: '1.5rem',
              }}>
                What grows beneath the surface makes everything else <em style={{ fontStyle: 'italic', color: 'var(--accent-warm)' }}>possible</em>.
              </motion.h1>
              <motion.p {...fade(0.3)} style={{ color: 'var(--text-secondary)', fontSize: '1.05rem', lineHeight: 1.7, marginBottom: '0.75rem' }}>
                UMI comes from <em>umami</em> - depth, savor, fullness - but also from what is unseen.
                We don't treat mushrooms as an ingredient. We treat them as teachers.
              </motion.p>
              <motion.p {...fade(0.35)} style={{ color: 'var(--text-secondary)', fontSize: '1.15rem', lineHeight: 1.65, marginBottom: 0 }}>
                UMI is a knowledge community built around fungi. This is a space for growers, cooks, and the genuinely curious. We write about fungi the way they deserve - with depth, rigor, and a refusal to dumb it down.
              </motion.p>
            </div>
          </>
        ))}

        {/* Journal Section */}
        <div id="journals" style={{ scrollMarginTop: '2rem' }}>
          <div className="container">

            {/* Filter bar: tags + search */}
            <motion.div {...fade(0.45)} className="filter-bar" style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              gap: '1rem', marginBottom: '2rem', flexWrap: 'wrap',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                <button
                  onClick={() => { setActiveTag(null); setPage(1); }}
                  style={{
                    padding: '0.45rem 1.1rem', borderRadius: '8px',
                    fontSize: '0.82rem', fontFamily: 'var(--font-sans)',
                    cursor: 'pointer', border: '1px solid',
                    transition: 'all 0.2s',
                    background: !activeTag ? 'var(--text-primary)' : 'var(--bg-sidebar)',
                    color: !activeTag ? 'var(--bg-body)' : 'var(--text-primary)',
                    borderColor: !activeTag ? 'var(--text-primary)' : 'var(--border-subtle)',
                  }}
                >
                  All
                </button>
                {allTags.map((tag) => (
                  <button
                    key={tag}
                    onClick={() => { setActiveTag(tag === activeTag ? null : tag); setPage(1); }}
                    style={{
                      padding: '0.45rem 1.1rem', borderRadius: '8px',
                      fontSize: '0.82rem', fontFamily: 'var(--font-sans)',
                      cursor: 'pointer', border: '1px solid',
                      transition: 'all 0.2s',
                      background: activeTag === tag ? 'var(--text-primary)' : 'var(--bg-sidebar)',
                      color: activeTag === tag ? 'var(--bg-body)' : 'var(--text-primary)',
                      borderColor: activeTag === tag ? 'var(--text-primary)' : 'var(--border-subtle)',
                    }}
                  >
                    {tag}
                  </button>
                ))}
              </div>
              <div className="search-row" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0 }}>
                <div style={{ position: 'relative', flex: 1 }}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--text-tertiary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ position: 'absolute', left: '0.7rem', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
                    <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                  </svg>
                  <input
                    type="text"
                    placeholder="Search"
                    value={searchQuery}
                    onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
                    className="search-input"
                    style={{
                      padding: '0 0.75rem 0 2rem',
                      borderRadius: '8px',
                      border: '1px solid var(--border-subtle)',
                      background: 'var(--bg-sidebar)',
                      fontSize: '0.82rem',
                      fontFamily: 'var(--font-sans)',
                      color: 'var(--text-primary)',
                      outline: 'none',
                      width: '160px',
                      height: '32px',
                      boxSizing: 'border-box',
                      transition: 'border-color 0.2s',
                    }}
                    onFocus={(e) => { e.target.style.borderColor = 'var(--text-primary)'; }}
                    onBlur={(e) => { e.target.style.borderColor = 'var(--border-subtle)'; }}
                  />
                </div>
                {user?.role === 'admin' && (
                  <Link to="/admin/new" style={{
                    padding: '0 0.85rem', borderRadius: '8px',
                    fontSize: '0.82rem', fontFamily: 'var(--font-sans)',
                    background: 'var(--text-primary)', color: 'var(--bg-body)',
                    border: '1px solid var(--text-primary)',
                    display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
                    whiteSpace: 'nowrap',
                    height: '32px', lineHeight: 1,
                    boxSizing: 'border-box',
                  }}>
                    + New Post
                  </Link>
                )}
              </div>
            </motion.div>

            {/* Post grid: uniform 3-column */}
            {!loading && filteredPosts.length > 0 && (
              <>
                <div className="journal-grid" style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: '1.25rem',
                }}>
                  {paginatedPosts.map((p, i) => (
                    <BlogCard key={p.id} post={p} index={i} />
                  ))}
                </div>

                {totalPages > 1 && (
                  <div style={{
                    display: 'flex', justifyContent: 'center', alignItems: 'center',
                    gap: '0.5rem', marginTop: '2.5rem',
                  }}>
                    <button
                      onClick={() => { setPage((p) => Math.max(1, p - 1)); document.getElementById('journals')?.scrollIntoView({ behavior: 'smooth' }); }}
                      disabled={page === 1}
                      style={{
                        padding: '0.4rem 0.9rem', borderRadius: '8px', fontSize: '0.82rem',
                        fontFamily: 'var(--font-sans)', cursor: page === 1 ? 'default' : 'pointer',
                        border: '1px solid var(--border-subtle)', background: 'var(--bg-sidebar)',
                        color: page === 1 ? 'var(--text-tertiary)' : 'var(--text-primary)',
                        opacity: page === 1 ? 0.5 : 1, transition: 'all 0.2s',
                      }}
                    >
                      Prev
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
                      <button
                        key={n}
                        onClick={() => { setPage(n); document.getElementById('journals')?.scrollIntoView({ behavior: 'smooth' }); }}
                        style={{
                          width: '34px', height: '34px', borderRadius: '8px', fontSize: '0.82rem',
                          fontFamily: 'var(--font-sans)', cursor: 'pointer',
                          border: '1px solid', transition: 'all 0.2s',
                          background: page === n ? 'var(--text-primary)' : 'var(--bg-sidebar)',
                          color: page === n ? 'var(--bg-body)' : 'var(--text-primary)',
                          borderColor: page === n ? 'var(--text-primary)' : 'var(--border-subtle)',
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        }}
                      >
                        {n}
                      </button>
                    ))}
                    <button
                      onClick={() => { setPage((p) => Math.min(totalPages, p + 1)); document.getElementById('journals')?.scrollIntoView({ behavior: 'smooth' }); }}
                      disabled={page === totalPages}
                      style={{
                        padding: '0.4rem 0.9rem', borderRadius: '8px', fontSize: '0.82rem',
                        fontFamily: 'var(--font-sans)', cursor: page === totalPages ? 'default' : 'pointer',
                        border: '1px solid var(--border-subtle)', background: 'var(--bg-sidebar)',
                        color: page === totalPages ? 'var(--text-tertiary)' : 'var(--text-primary)',
                        opacity: page === totalPages ? 0.5 : 1, transition: 'all 0.2s',
                      }}
                    >
                      Next
                    </button>
                  </div>
                )}
              </>
            )}

            {loading && (
              <p style={{ color: 'var(--text-tertiary)', fontSize: '0.9rem' }}>Loading journal...</p>
            )}

            {!loading && filteredPosts.length === 0 && (
              <p style={{ color: 'var(--text-tertiary)', fontSize: '0.9rem', fontStyle: 'italic' }}>
                {activeTag || searchQuery.trim()
                  ? 'No matching posts found.'
                  : 'The journal is quiet for now. Check back soon.'}
              </p>
            )}

          </div>
        </div>

        {/* Subscribe */}
        <div className="container">
          <SubscribeBox />
        </div>

      </div>
    </PageTransition>
  );
}

